import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useCheckUserInvitationMutation,
  useCompleteCourseMutation,
  useGetCourseByIdQuery,
  useInviteToCourseMutation,
} from "../apis/coursesAPI";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Link,
  MobileStepper,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { mobileWidth, stepperVariant } from "../styleUtils";
import { Slide } from "./components/slide";
import { useNavigate, useSearchParams } from "react-router-dom";
import colors from "../colors";
import { ViewContainer } from "./components/viewContainer";
import { useTranslation } from "react-i18next";
import config from "../config";
import { theme } from "../theme";
import {
  useAddRoleMutation,
  useCheckEmailVerificationMutation,
  useEditRoleMutation,
  useGetMissingFieldsQuery,
  useVerifyEmailMutation,
} from "../apis/rolesAPI";
import { MissingFieldsQuery } from "./components/missingFieldsQuery";
import { IRole } from "@backend/model/role";
import {
  INewUserInfo,
  QrUserVerificationModal,
} from "./components/modals/qrUserVerificationModal";
import i18n from "../i18n";
import { useListSettingsQuery } from "../apis/settingsAPI";

const getSlideIndex = (index: number) => `slide-${index}`;

export const PlayCourse = () => {
  const { t } = useTranslation();
  const onChangeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const [searchParams] = useSearchParams();
  const userId = useMemo(
    () => searchParams.get("userId")?.replace(" ", "+"),
    [searchParams]
  );
  const [password, setPassword] = useState(
    userId === config.publicCourseUserId ? config.publicCoursePassword : ""
  );
  const [tempPassword, setTempPassword] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const courseId = useMemo(() => searchParams.get("courseId"), [searchParams]);
  const apiKey = useMemo(() => searchParams.get("apiKey"), [searchParams]);
  const qrUser = useMemo(() => searchParams.get("qrUser"), [searchParams]);
  const qrNewUser = useMemo(() => searchParams.get("newUser"), [searchParams]);
  const [currentQrUser, setCurrentQrUser] = useState<string | undefined>(
    undefined
  );
  const orgId = useMemo(
    () => searchParams.get("organisationId"),
    [searchParams]
  );

  const [error, setError] = useState(false);

  const [showQrUserModal, setShowQrUserModal] = useState(
    !!(qrUser || qrNewUser)
  );

  const [addRole, { isLoading: addRoleLoading, isSuccess: addRoleIsSuccess }] =
    useAddRoleMutation();

  const [
    verifyEmail,
    { isLoading: verifyEmailLoading, isSuccess: verifyEmailIsSuccess },
  ] = useVerifyEmailMutation();

  const [
    checkEmailVerification,
    {
      isLoading: checkEmailVerificationLoading,
      isSuccess: checkEmailVerificationIsSuccess,
    },
  ] = useCheckEmailVerificationMutation();

  const organisationId = useMemo(
    () => searchParams.get("organisationId"),
    [searchParams]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [slideReady, setSlideReady] = useState<{
    [slideIndex: string]: boolean;
  }>({});

  const [showPassModal, setShowPassModal] = useState(
    !!(userId && userId !== config.publicCourseUserId && apiKey)
  );

  const {
    data,
    isFetching: loadingCourse,
    error: dataError,
  } = useGetCourseByIdQuery(
    {
      organisationId: organisationId!,
      courseId: courseId!,
      userId: userId || "",
      apiKey: apiKey || "",
      password,
    },
    {
      skip: !organisationId || !courseId || !!(apiKey && !password),
    }
  );

  // Check if the user is invited to the course.
  const [checkUserInvitation] = useCheckUserInvitationMutation();

  const doUserCheck = async (username: string) => {
    let checkedUser = { userId: "" };
    if (organisationId && userId && data && apiKey && password) {
      checkedUser = await checkUserInvitation({
        organisationId,
        userId,
        username: username,
        courseId: data.id,
        apiKey,
        password,
      }).unwrap();
    }
    return checkedUser;
  };

  const getUserStatusFromNewUserFields = () => {
    if (
      data &&
      data.newUserFields &&
      data.newUserFields.length == 1 &&
      data.newUserFields[0].toString() == "contractorCompanyVisible"
    ) {
      return "contractor";
    }
    if (
      data &&
      data.newUserFields &&
      data.newUserFields.length > 0 &&
      data.newUserFields[0].toString().toLowerCase().includes("visitor")
    ) {
      return "visitor";
    } else if (
      data &&
      data.newUserFields &&
      data.newUserFields.length > 0 &&
      data.newUserFields[0].toString().toLowerCase().includes("contractor")
    ) {
      return "contractor";
    } else if (
      data &&
      data.newUserFields &&
      data.newUserFields.length > 0 &&
      !data.newUserFields[0].toString().toLowerCase().includes("contractor") &&
      !data.newUserFields[0].toString().toLowerCase().includes("visitor")
    ) {
      return "employee";
    } else return "contractor"; // default contractor, jos newUserFields on tyhjä
  };

  // Add user.
  const addUser = async (role: INewUserInfo) => {
    console.log(role);
    const userRole: IRole = {
      firstName: role.firstName,
      lastName: role.lastName,
      company: role.company,
      email: role.email,
      phoneNumber: role.phoneNumber || "",
      taxNumber: role.taxNumber || "",
      occupationalSafetyCard: role.occupationalSafetyCard || "",
      hotWorkCard: role.hotWorkCard || "",
      shoeSize: role.shoeSize || "",
      jacketSize: role.jacketSize || "",
      dietaryRestrictions: role.dietaryRestrictions || "",
      visitationDate: role.visitationDate || "",
      role: "user",
      organisationId: organisationId || "",
      id: "",
      userStatus: getUserStatusFromNewUserFields(),
    };
    try {
      const roleAdded = await addRole({
        ...userRole,
        apiKey: apiKey || "",
        courseId: courseId || "",
        password: password || "",
        userId: userId || "",
      } as IRole).unwrap();
      // Set saved user id to current QR user, so we can mark the course completed.
      let newUserId = "";
      if (roleAdded.id) {
        setCurrentQrUser(roleAdded.id);
        newUserId = roleAdded.id;
      } else {
        console.log(roleAdded.error);
        setError(true);
        return false;
      }
    } catch (err) {
      setError(true);
    }
    return true;
  };

  const sendVerification = async (email: string) => {
    try {
      const emailSent = await verifyEmail({
        organisationId: organisationId || "",
        apiKey: apiKey || "",
        courseId: courseId || "",
        password: password || "",
        userId: userId || "",
        email: email,
      }).unwrap();

      if (emailSent) {
        return true;
      } else return false;
    } catch (err) {
      setError(true);
    }
    return false;
  };

  const checkVerification = async (
    email: string,
    verificationString: string
  ) => {
    try {
      const emailChecked = await checkEmailVerification({
        organisationId: organisationId || "",
        apiKey: apiKey || "",
        courseId: courseId || "",
        password: password || "",
        userId: userId || "",
        email: email,
        verificationString: verificationString,
      }).unwrap();

      if (emailChecked) {
        return true;
      } else return false;
    } catch (err) {
      setError(true);
    }
    return false; //
  };

  const {
    data: missingFields,
    isFetching: loadingMissingFields,
    error: missingFieldsError,
  } = useGetMissingFieldsQuery(
    {
      organisationId: organisationId!,
      courseId: courseId!,
      userId: userId || "",
      apiKey: apiKey || "",
      password,
    },
    {
      skip: !organisationId || !courseId || !apiKey || !password,
    }
  );

  const [completeCourse] = useCompleteCourseMutation();
  const [editRole, { isLoading: editRoleLoading }] = useEditRoleMutation();

  const selectedSlide = useMemo(
    () =>
      data && data.slides.length >= selectedIndex
        ? data.slides[selectedIndex]
        : undefined,
    [selectedIndex, data]
  );

  useEffect(() => {
    if (
      data &&
      selectedIndex >= data.slides.length - 1 &&
      !completed &&
      slideReady[getSlideIndex(selectedIndex)]
    ) {
      // check if the course is public
      if (apiKey) {
        if ((qrUser || qrNewUser) && currentQrUser) {
          console.log("Public course completed");
          setCompleted(true);
        } else if (password && password !== config.publicCoursePassword) {
          console.log("Complete emailed course");
          setCompleted(true);
        } else console.log("Public course finished");
      } else {
        console.log("Course completed");
        setCompleted(true);
      }
    }
  }, [selectedIndex, data, completed, slideReady]);

  useEffect(() => {
    const doComplete = async () => {
      // Complete course with a regular user
      if (
        organisationId &&
        userId &&
        data &&
        apiKey &&
        userId !== config.publicCourseEmail
      ) {
        await completeCourse({
          organisationId,
          userId,
          courseId: data.id,
          apiKey,
          password,
        }).unwrap();
      }
    };

    // Complete course if a public course user is checked.
    const doCompleteQrUser = async () => {
      if (organisationId && currentQrUser && data && apiKey && userId) {
        await completeCourse({
          organisationId,
          userId,
          courseId: data.id,
          apiKey,
          password,
          targetUserId: currentQrUser,
        }).unwrap();
      }
    };

    if (completed && !currentQrUser) doComplete();
    if (completed && currentQrUser) doCompleteQrUser();
    if (data && showPassModal) {
      setShowPassModal(false);
    }
  }, [
    completed,
    data,
    organisationId,
    completeCourse,
    userId,
    apiKey,
    password,
    showPassModal,
  ]);

  const onSlideReady = useCallback(() => {
    console.log(`slide ${selectedIndex} is now ready`);
    if (!slideReady[getSlideIndex(selectedIndex)]) {
      setSlideReady((val) =>
        Object.assign({}, val, { [getSlideIndex(selectedIndex)]: true })
      );
    }
  }, [selectedIndex, slideReady]);

  const narrowScreen = useMediaQuery(mobileWidth);

  return (
    <ViewContainer
      noPadding={narrowScreen}
      loading={loadingCourse || editRoleLoading}
    >
      <Box
        flex={1}
        display={"flex"}
        height={"100%"}
        flexDirection={"column"}
        position={"relative"}
        marginTop={!!narrowScreen && !!apiKey ? theme.spacing(2) : 0} // Leaves a little room for language selector.
      >
        {selectedSlide && (
          <Box sx={{ height: narrowScreen ? `calc(100% - 60px)` : "100%" }}>
            <Slide
              key={`slide-${selectedIndex}`}
              slide={selectedSlide}
              organisationId={organisationId || ""}
              slideUpdated={() => {}}
              edit={false}
              setReady={onSlideReady}
              marginBottom={theme.spacing(5)}
              password={password}
            />
          </Box>
        )}
        <MobileStepper
          variant={stepperVariant(narrowScreen, data?.slides.length || 0)}
          sx={{
            position: apiKey ? "fixed" : narrowScreen ? "relative" : "absolute",
            bottom: narrowScreen ? theme.spacing(1) : 0,
            zIndex: 2,
            paddingBottom: 0,
            marginBottom: -1,
            height: 60,
          }}
          position="bottom"
          backButton={
            <Button
              variant="contained"
              onClick={() => setSelectedIndex(selectedIndex - 1)}
              disabled={!(selectedIndex > 0)}
            >
              {t("previousSlide")}
            </Button>
          }
          nextButton={
            data && selectedIndex < data.slides.length - 1 ? (
              <Button
                variant="contained"
                disabled={!slideReady[getSlideIndex(selectedIndex)]}
                onClick={() => setSelectedIndex(selectedIndex + 1)}
              >
                {t("nextSlide")}
              </Button>
            ) : (
              <Box width={112} />
            )
          }
          steps={data?.slides.length || 1}
          activeStep={selectedIndex}
        />
        <Modal
          open={showPassModal || !!(missingFields && missingFields.length)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Card
            style={{
              background: colors.backgroundSecondary,
              maxHeight: "90%",
              overflow: "auto",
            }}
          >
            {courseId && showPassModal && (
              <Box
                maxWidth={500}
                padding={theme.spacing(2)}
                alignItems={"center"}
                display={"flex"}
                flexDirection={"column"}
              >
                <Typography
                  variant="h4"
                  style={{
                    marginBottom: theme.spacing(2),
                    textAlign: "center",
                  }}
                >
                  {t("welcomeToCourse")}
                </Typography>
                <Typography variant="body1" textAlign={"center"}>
                  {t("tosDisclaimer")}
                </Typography>
                <Box flexDirection="column" margin={theme.spacing(2)}>
                  <Checkbox
                    checked={tosAccepted}
                    onChange={(e) => {
                      setTosAccepted(e.target.checked);
                    }}
                  />
                  <Typography variant="caption">
                    {`${t("readedTos")} `}
                    <Link
                      href="https://www.inguider.fi/termsofservice"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("tos")}
                    </Link>
                  </Typography>
                </Box>
                {dataError && (dataError as any).originalStatus === 401 && (
                  <Typography variant="caption" color={"red"}>
                    {t("checkPassword")}
                  </Typography>
                )}
                <TextField
                  label={t("password")}
                  value={tempPassword}
                  style={{ margin: theme.spacing(1) }}
                  onChange={(e) =>
                    setTempPassword(e.target.value.toUpperCase())
                  }
                />

                {loadingCourse && <CircularProgress />}
                {!loadingCourse && (
                  <Button
                    variant="contained"
                    disabled={!tempPassword || !tosAccepted}
                    onClick={() => {
                      setPassword(tempPassword);
                    }}
                  >
                    {t("confirm")}
                  </Button>
                )}
              </Box>
            )}
            {missingFields && missingFields.length && (
              <MissingFieldsQuery
                missingFields={missingFields}
                onSave={(personData: IRole) => {
                  editRole({
                    ...personData,
                    id: userId || "",
                    organisationId: organisationId || "",
                    courseId: courseId || "",
                    apiKey: apiKey || "",
                    password,
                  });
                }}
                loading={editRoleLoading}
              />
            )}
          </Card>
        </Modal>
        {!!(qrUser || qrNewUser) && !addRoleIsSuccess && (
          <QrUserVerificationModal
            newUserFields={data?.newUserFields || []}
            isVisible={showQrUserModal}
            setVisible={(value) => setShowQrUserModal(value)}
            newUser={!!qrNewUser}
            saveUser={(user) => {
              setCurrentQrUser(user);
            }}
            checkUser={(user) => {
              return doUserCheck(user);
            }}
            addUser={(role) => {
              return addUser(role);
            }}
            // verification callbacks:
            sendVerificationEmail={(email: string) => {
              return sendVerification(email);
            }}
            checkVerificationString={(
              email: string,
              verificationString: string
            ) => {
              return checkVerification(email, verificationString);
            }}
          />
        )}
        {/* Error modal */}
        <Modal
          open={error}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: theme.spacing(3),
              background: colors.backgroundSecondary,
            }}
          >
            <Typography>{t("errorOccured")}</Typography>
            <Button
              variant="text"
              style={{ paddingTop: theme.spacing(2) }}
              onClick={() => {
                setError(false);
              }}
            >
              {t("ok")}
            </Button>
          </Card>
        </Modal>
      </Box>
      {/* If the user is not logged in, show the language selector. */}
      {apiKey ? (
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            zIndex: 1500,
          }}
        >
          <Button
            color="inherit"
            aria-label="english"
            onClick={() => onChangeLanguage("en")}
          >
            EN
          </Button>
          <Button
            color="inherit"
            aria-label="finnish"
            onClick={() => onChangeLanguage("fi")}
          >
            FI
          </Button>
        </Box>
      ) : (
        <></>
      )}
    </ViewContainer>
  );
};
